import React, { useState, useEffect, useRef } from 'react'
import { Input } from './ui/input'
import { cn } from '../lib/utils'

/**
 * CurrencyInput - Input with automatic currency formatting
 * 
 * Features:
 * - Shows thousands separators and decimals while typing
 * - Displays currency symbol (USD, MXN, EUR)
 * - Stores clean numeric value internally
 * - Mobile-friendly with numeric keyboard
 * 
 * @param {string} value - Numeric string value
 * @param {function} onChange - Called with clean numeric string
 * @param {string} currency - Currency code (USD, MXN, EUR)
 * @param {string} placeholder - Input placeholder
 * @param {string} className - Additional classes
 */
export function CurrencyInput({
  value,
  onChange,
  currency = 'MXN',
  placeholder = '0.00',
  className,
  ...props
}) {
  const [displayValue, setDisplayValue] = useState('')
  const inputRef = useRef(null)
  
  // Currency symbols
  const currencySymbols = {
    USD: '$',
    MXN: '$',
    EUR: '€'
  }
  
  const symbol = currencySymbols[currency] || '$'
  
  // Format number to display string with separators
  const formatForDisplay = (numericValue) => {
    if (!numericValue || numericValue === '') return ''
    
    const num = parseFloat(numericValue)
    if (isNaN(num)) return ''
    
    // Use locale formatting with the appropriate currency
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(num)
  }
  
  // Parse display string back to clean numeric value
  const parseToNumeric = (displayStr) => {
    if (!displayStr) return ''
    // Remove all non-numeric characters except decimal point
    const cleaned = displayStr.replace(/[^0-9.]/g, '')
    // Ensure only one decimal point
    const parts = cleaned.split('.')
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('')
    }
    return cleaned
  }
  
  // Update display when external value changes
  useEffect(() => {
    if (value !== undefined && value !== null) {
      setDisplayValue(formatForDisplay(value))
    } else {
      setDisplayValue('')
    }
  }, [value])
  
  // Handle input change
  const handleChange = (e) => {
    const inputValue = e.target.value
    
    // If user cleared the input
    if (!inputValue) {
      setDisplayValue('')
      onChange('')
      return
    }
    
    // Parse to get clean numeric
    const numeric = parseToNumeric(inputValue)
    
    // Validate it's a valid number format
    if (numeric && !/^\d*\.?\d*$/.test(numeric)) {
      return // Invalid input, ignore
    }
    
    // Update display and call onChange with clean value
    setDisplayValue(inputValue.replace(/[^0-9.,]/g, ''))
    onChange(numeric)
  }
  
  // Format on blur - ensure 2 decimals
  const handleBlur = () => {
    if (value) {
      const num = parseFloat(value)
      if (!isNaN(num)) {
        // Format with thousands separators and exactly 2 decimals
        const formatted = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(num)
        setDisplayValue(formatted)
        // Also update the actual value to have 2 decimals
        onChange(num.toFixed(2))
      }
    }
  }
  
  // On focus, show raw number for easier editing
  const handleFocus = () => {
    if (value) {
      setDisplayValue(value)
    }
  }
  
  return (
    <div className="relative">
      <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium pointer-events-none">
        {currency}
      </div>
      <Input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={cn("pl-14 text-right font-mono", className)}
        {...props}
      />
    </div>
  )
}

/**
 * Format currency for display (read-only)
 * @param {number} value - Numeric value
 * @param {string} currency - Currency code
 * @param {boolean} showSign - Show +/- sign
 * @returns {string} Formatted currency string
 */
export function formatCurrencyDisplay(value, currency = 'MXN', showSign = false) {
  if (value === null || value === undefined || isNaN(value)) return '-'
  
  // Format number with thousands separator and 2 decimals
  const formatted = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(Math.abs(value))
  
  // Build result with currency code prefix
  let result = `${currency} ${formatted}`
  
  if (showSign) {
    return value >= 0 ? `+${result}` : `-${result}`
  }
  
  return value < 0 ? `-${result}` : result
}

export default CurrencyInput
